'use client';

import dynamic from 'next/dynamic';
import { Fragment, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logCellCoverageCalloutTapped from '@alltrails/amplitude/events/logCellCoverageCalloutTapped';
import LinkOrNativeAnchor from '@alltrails/denali/components/LinkOrNativeAnchor';
import TableRow from '@alltrails/denali/components/TableRow';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import { TrailPage as TrailPageType } from '@alltrails/trails';
import type TrailDetails from '@alltrails/types/TrailDetails';
import type TrailHighlight from '@alltrails/types/TrailHighlight';
import getTrailHighlightIcon from './getTrailHighlightIcon';
import styles from './TrailHighlights.module.scss';

const SpottyServiceModal = dynamic(() => import('./SpottyServiceModal'), { ssr: false });
const CellConnectivityModal = dynamic(() => import('../CellConnectivity/CellConnectivityModal'), { ssr: false });
const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });

type TrailHighlightsProps = {
  highlights: TrailHighlight[];
  trailDetails: TrailDetails;
  trailPage: TrailPageType;
};

const TrailHighlights = ({ highlights, trailDetails, trailPage }: TrailHighlightsProps) => {
  const isMobile = useIsMobileSizedScreen();
  const [isSpottyServiceOpen, setIsSpottyServiceOpen] = useState(false);
  const [isDownloadInAppOpen, setIsDownloadInAppOpen] = useState(false);

  const openSpottyService = () => {
    logCellCoverageCalloutTapped({ trail_id: trailDetails.id });
    setIsSpottyServiceOpen(true);
  };

  const openDownloadInAppFromSpottyService = () => {
    setIsSpottyServiceOpen(false);
    setIsDownloadInAppOpen(true);
  };

  return (
    <div className={styles.container}>
      {highlights.map(highlight => {
        const Icon = getTrailHighlightIcon(highlight.type);
        const isLowCellService = highlight.type === 'info_low-cell-service';
        const row = (
          <TableRow
            className={styles.row}
            label={<span className={styles.title}>{highlight.title}</span>}
            description={highlight.subtitle}
            leftContent={<Icon />}
            rightContent={highlight.slug || isLowCellService ? <ChevronSm orientation="right" color="--color-text-primary" /> : undefined}
          />
        );

        if (isLowCellService) {
          return (
            <button key={highlight.type} type="button" className={styles.button} onClick={openSpottyService}>
              {row}
            </button>
          );
        }

        if (highlight.slug) {
          return (
            <LinkOrNativeAnchor key={highlight.type} href={`/${highlight.slug}`} className={styles.link} target="_blank">
              {row}
            </LinkOrNativeAnchor>
          );
        }

        return <Fragment key={highlight.type}>{row}</Fragment>;
      })}
      {isSpottyServiceOpen &&
        (isMobile ? (
          <CellConnectivityModal
            isOpen={isSpottyServiceOpen}
            onCloseRequest={() => setIsSpottyServiceOpen(false)}
            trailDetails={trailDetails}
          />
        ) : (
          <SpottyServiceModal
            isOpen={isSpottyServiceOpen}
            onCloseRequest={() => setIsSpottyServiceOpen(false)}
            onDownloadInApp={openDownloadInAppFromSpottyService}
          />
        ))}
      {isDownloadInAppOpen && (
        <HitTheTrailModal
          includeDownloadInApp={false}
          includeExportMapFile={false}
          includeOpenInApp={false}
          includeSendToGarmin={false}
          isOpen={isDownloadInAppOpen}
          onCloseRequest={() => setIsDownloadInAppOpen(false)}
          steps={['download']}
          title={<FormattedMessage defaultMessage="Download in app" />}
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}
    </div>
  );
};

export default TrailHighlights;
